import { getMediaFileKind, isMediaFileModerated, type MediaFileConnectionDetails, MediaFileKind } from "@next-platform/client-common-core";
import getMediaFileSecondaryConnections from "@next-platform/client-common-core/viewMedia/common/getMediaFileSecondaryConnections";
import type { MediaFileMetadataRowKind } from "@next-platform/client-common-core/viewMedia/metadata/buildMediaFileMetadataRows";
import buildMediaFileMetadataRows from "@next-platform/client-common-core/viewMedia/metadata/buildMediaFileMetadataRows";
import getMediaFileCharacterLabel from "@next-platform/client-common-core/viewMedia/metadata/getMediaFileCharacterLabel";
import type { MediaFilePrimaryCharacterDetails } from "@next-platform/client-common-core/viewMedia/types/MediaFileCharacters";
import type {
  MediaFileDetailsCategory,
  MediaFileDetailsConnection,
  MediaFileDetailsContent,
  MediaFileDetailsLabels,
  MediaFileDetailsMetadataRow,
} from "@next-platform/client-common-spicy-ui";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import { type MediaFile, MediaFileUsedAs, MediaFileVisibility, type MediaFileWithPublicFields } from "@next-platform/packages-ts-types";

import getMediaFileConnectionPath from "@/pages/Gallery/components/MediaFilePreview/getMediaFileConnectionPath";
import mediaFileKindIcons from "@/pages/Gallery/components/MediaFilePreview/mediaFileKindIcons";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

import formatMetadataValue from "./utils/formatMetadataValue";

type LiveMediaFile = MediaFile | MediaFileWithPublicFields;

type Translate = (key: string, options?: { defaultValue: string }) => string;

const LIST_METADATA_KIND_ORDER: MediaFileMetadataRowKind[] = ["createdAt", "model", "dimensions"];

const MEDIA_FILE_METADATA_ICONS: Partial<Record<MediaFileMetadataRowKind, IconVariants>> = {
  model: IconVariants.WAND_SPARKLES,
  dimensions: IconVariants.IMAGE,
  size: IconVariants.FILE,
  source: IconVariants.IMAGE,
  visibility: IconVariants.EARTH,
  createdAt: IconVariants.CALENDAR,
  usedAs: IconVariants.SQUARE_USER,
  tags: IconVariants.TAG,
  connections: IconVariants.LINK,
};

const METADATA_KIND_I18N_KEYS: Record<MediaFileMetadataRowKind, string> = {
  model: "mediaFile:metadata.model",
  dimensions: "mediaFile:metadata.dimensions",
  size: "mediaFile:metadata.size",
  source: "mediaFile:metadata.source",
  visibility: "mediaFile:metadata.visibility",
  createdAt: "mediaFile:metadata.createdAt",
  usedAs: "mediaFile:metadata.usedAs",
  tags: "mediaFile:metadata.tags",
  connections: "mediaFile:metadata.connections",
  prompt: "mediaFile:metadata.prompt",
};

// The same icons the grid tile shows, so one file reads the same on both surfaces.
const USED_AS_ICONS: Partial<Record<MediaFileUsedAs, IconVariants>> = {
  [MediaFileUsedAs.CHARACTER_AVATAR]: mediaFileKindIcons[MediaFileKind.CHARACTER_AVATAR],
  [MediaFileUsedAs.PERSONA_AVATAR]: mediaFileKindIcons[MediaFileKind.PERSONA_AVATAR],
  [MediaFileUsedAs.USER_AVATAR]: mediaFileKindIcons[MediaFileKind.USER_AVATAR],
  [MediaFileUsedAs.CONVERSATION_IMAGE]: mediaFileKindIcons[MediaFileKind.CONVERSATION_IMAGE],
  [MediaFileUsedAs.LOREBOOK_AVATAR]: mediaFileKindIcons[MediaFileKind.LOREBOOK_AVATAR],
};

const USED_AS_I18N_KEYS: Record<MediaFileUsedAs, string> = {
  [MediaFileUsedAs.CHARACTER_AVATAR]: "mediaFile:usedAs.characterAvatar",
  [MediaFileUsedAs.PERSONA_AVATAR]: "mediaFile:usedAs.personaAvatar",
  [MediaFileUsedAs.USER_AVATAR]: "mediaFile:usedAs.userAvatar",
  [MediaFileUsedAs.CONVERSATION_IMAGE]: "mediaFile:usedAs.conversationImage",
  [MediaFileUsedAs.CHARACTER_MEDIA_ASSETS]: "mediaFile:usedAs.characterMediaAssets",
  [MediaFileUsedAs.LOREBOOK_AVATAR]: "mediaFile:usedAs.lorebookAvatar",
};

export function buildMediaFileDetailsLabels(translate: Translate): MediaFileDetailsLabels {
  return {
    title: translate("mediaFile:details.title"),
    imageAlt: translate("gallery:mediaFilePreview.imageAlt"),
    copyLink: translate("mediaFile:details.copyLink"),
    downloadAriaLabel: translate("mediaFile:details.downloadAriaLabel"),
    openOriginal: translate("mediaFile:details.openOriginal"),
    keepAriaLabel: translate("mediaFile:details.keepAriaLabel"),
    keptLabel: translate("mediaFile:details.keptLabel", { defaultValue: translate("mediaFile:details.keepAriaLabel") }),
    moreAriaLabel: translate("mediaFile:details.moreAriaLabel"),
    unkeepLabel: translate("mediaFile:details.unkeepLabel", { defaultValue: "Un-keep" }),
    makePrivateLabel: translate("mediaFile:details.makePrivateLabel", { defaultValue: "Make private" }),
    addToFolderLabel: translate("mediaFile:details.addToFolderLabel", { defaultValue: "Add to folder" }),
    reportLabel: translate("mediaFile:details.reportLabel", { defaultValue: "Report" }),
    deleteLabel: translate("mediaFile:details.deleteLabel", { defaultValue: "Delete" }),
    promptLabel: translate("mediaFile:metadata.prompt"),
    avatarOf: translate("mediaFile:details.avatarOf"),
    openCharacterAriaLabel: translate("mediaFile:details.openCharacterAriaLabel"),
    visibilityPublic: translate("mediaFile:details.visibility.public"),
    visibilityPublicDescription: translate("mediaFile:details.visibility.publicDescription"),
    // The same word the grid tile's mature icon carries.
    nsfw: translate("gallery:mediaFilePreview.mature"),
    notFound: translate("mediaFile:errors.notFound"),
    loadFailed: translate("mediaFile:errors.loadFailed"),
    retry: translate("mediaFile:errors.retry"),
  };
}

function getUsedAsLabel({ usedAs, translate }: { usedAs: string; translate: Translate }): string {
  if (usedAs in USED_AS_I18N_KEYS) {
    return translate(USED_AS_I18N_KEYS[usedAs as MediaFileUsedAs]);
  }
  return usedAs;
}

// The chip row follows the tile's kind: a file attached to nothing reads "No relation" and a chat upload reads
// "Uploaded", each with the tile's icon; any other file shows its uses, avatars included.
function mapUsedAsCategories({
  file,
  usedAsValues,
  translate,
}: {
  file: LiveMediaFile;
  usedAsValues: string[];
  translate: Translate;
}): MediaFileDetailsCategory[] {
  const kind = getMediaFileKind(file);
  if (kind === MediaFileKind.UNATTACHED) {
    return [{ id: kind, label: translate("gallery:mediaFilePreview.noRelation"), icon: mediaFileKindIcons[kind] }];
  }
  if (kind === MediaFileKind.UPLOADED) {
    return [{ id: kind, label: translate("mediaFile:kind.uploaded"), icon: mediaFileKindIcons[kind] }];
  }
  return usedAsValues.map((usedAs) => ({
    id: usedAs,
    label: getUsedAsLabel({ usedAs, translate }),
    ...(USED_AS_ICONS[usedAs as MediaFileUsedAs] ? { icon: USED_AS_ICONS[usedAs as MediaFileUsedAs] } : {}),
  }));
}

function getMetadataKindLabel({ kind, translate }: { kind: MediaFileMetadataRowKind; translate: Translate }): string {
  return translate(METADATA_KIND_I18N_KEYS[kind]);
}

function mapConnectionLink({
  connection,
  localizePath,
}: {
  connection: MediaFileConnectionDetails | MediaFilePrimaryCharacterDetails;
  localizePath: (pathname: string) => string;
}): MediaFileDetailsConnection {
  return {
    id: connection.id,
    name: connection.name,
    href: localizePath(getMediaFileConnectionPath(connection)),
    imageUrl: connection.imageUrl ? (getAvatarUrl(connection.imageUrl, { preset: AvatarPreset.AVATAR_48X48 }) ?? undefined) : undefined,
  };
}

export function mapMediaFileDetailsContent({
  file,
  translate,
  locale,
  primaryCharacter,
  connections,
  isBlurred,
  localizePath,
}: {
  file: LiveMediaFile;
  translate: Translate;
  locale: string;
  primaryCharacter?: MediaFilePrimaryCharacterDetails;
  connections: readonly MediaFileConnectionDetails[];
  /** Blur this reader's view; the NSFW chip still follows the file itself. */
  isBlurred: boolean;
  localizePath: (pathname: string) => string;
}): MediaFileDetailsContent {
  const metadataRows = buildMediaFileMetadataRows(file);
  const usedAsValues = metadataRows.find((row) => row.kind === "usedAs")?.values ?? [];
  const visibilityValue = metadataRows.find((row) => row.kind === "visibility")?.values[0];
  const prompt = metadataRows.find((row) => row.kind === "prompt")?.values[0];

  const listRows: MediaFileDetailsMetadataRow[] = LIST_METADATA_KIND_ORDER.flatMap((kind) => {
    const row = metadataRows.find((metadataRow) => metadataRow.kind === kind);
    // The backend sends no model name for uploads and unresolved models; the row still shows, as N/A.
    if (!row && kind !== "model") {
      return [];
    }
    const value = row ? formatMetadataValue({ row, locale }) : translate("mediaFile:metadata.notAvailable");
    return [
      {
        id: kind,
        icon: MEDIA_FILE_METADATA_ICONS[kind] ?? IconVariants.IMAGE,
        label: getMetadataKindLabel({ kind, translate }),
        value,
      },
    ];
  });

  return {
    imageUrl: file.url,
    // An unjudged file reads `is_nsfw: false` because nobody looked, so the chip waits for a verdict like the tile does.
    isNsfw: file.is_nsfw && isMediaFileModerated(file),
    isBlurred,
    isKept: file.is_kept_by_me,
    isPublic: visibilityValue ? visibilityValue === MediaFileVisibility.PUBLIC : undefined,
    usedAsCategories: mapUsedAsCategories({ file, usedAsValues, translate }),
    primaryCharacter: primaryCharacter
      ? {
          ...mapConnectionLink({ connection: primaryCharacter, localizePath }),
          label: getMediaFileCharacterLabel({ kind: primaryCharacter.kind, translate }),
        }
      : undefined,
    connections: getMediaFileSecondaryConnections({ primaryCharacter, connections }).map((connection) =>
      mapConnectionLink({ connection, localizePath }),
    ),
    prompt,
    metadataRows: listRows,
  };
}
